import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { iconSubset } from './icons/icon-subset';
import { IconSetService } from '@coreui/icons-angular';
import { PrimeNGConfig } from 'primeng/api';
import { SessionService } from './services/session/session.service';
import { ThemeService } from './services/theme/theme.service';
import { AuthService } from '@auth0/auth0-angular';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '../environments/environment';

const IFCMYELIN_DOMAIN_MAP: Record<string, string> = {
    'develop.byod.ifcmyelin.com': 'byod.farmadvisor-dev.com',
    'staging.byod.ifcmyelin.com': 'byod.farmadvisor-stag.com',
    'byod.ifcmyelin.com':         'byod.farmadvisor.com',
};

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
    isLoading = toSignal(this.auth.isLoading$);
    readonly isDebug = environment.debug;

    /** New domain URL to redirect to, or null when not on a deprecated domain. */
    readonly deprecatedDomainUrl: string | null;
    /** The deprecated hostname being used (e.g. develop.byod.ifcmyelin.com). */
    readonly deprecatedHostname: string | null;

    private readonly document = inject(DOCUMENT);

    constructor(
        private auth: AuthService,
        private router: Router,
        private titleService: Title,
        private iconSetService: IconSetService,
        private primengConfig: PrimeNGConfig,
        private sessionService: SessionService,
        private destroyRef: DestroyRef,
        private themeService: ThemeService,
    ) {
        this.titleService.setTitle('BYOD');
        this.iconSetService.icons = { ...iconSubset };
        this.deprecatedHostname = this.document.location?.hostname ?? null;
        this.deprecatedDomainUrl = this.resolveDeprecatedDomainUrl();
    }

    ngOnInit() {
        this.primengConfig.ripple = true;
        this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((evt) => {
            if (!(evt instanceof NavigationEnd)) {
                return;
            }
        });

        this.sessionService.assumedRole$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }

    private resolveDeprecatedDomainUrl(): string | null {
        const location = this.document.location;
        if (!location) return null;
        const newHost = IFCMYELIN_DOMAIN_MAP[location.hostname];
        if (!newHost) return null;
        return `https://${newHost}${location.pathname}${location.search}${location.hash}`;
    }
}
